/**
 * Analytics Service
 * 
 * Singleton service that manages analytics tracking across the application.
 * Uses a provider pattern to allow swapping analytics backends.
 * 
 * Usage:
 *   import analytics, { AnalyticsEvents } from './services/analytics';
 *   
 *   // Initialize (once at app startup)
 *   analytics.init();
 *   
 *   // Track events
 *   analytics.track(AnalyticsEvents.RECIPE_VIEWED, { recipe_id: 123 });
 *   
 *   // Identify user on login
 *   analytics.identify(user.id, { email: user.email });
 */

import { PostHogProvider } from './PostHogProvider';
export { AnalyticsEvents, UserProperties } from './events';

class AnalyticsService {
  constructor() {
    this.provider = null;
    this.debugMode = false;
  }

  /**
   * Initialize analytics with PostHog (or swap provider here)
   * @param {Object} options - Configuration options
   * @param {boolean} options.isAdmin - Whether the user is an admin (disables session replay)
   * @param {Object} options.providerOptions - Additional provider-specific options
   */
  init(options = {}) {
    // Use PostHog by default - swap this line to change providers
    this.provider = new PostHogProvider();
    
    this.debugMode = options.debug || process.env.NODE_ENV === 'development';

    this.provider.init({
      apiKey: process.env.REACT_APP_POSTHOG_KEY,
      host: process.env.REACT_APP_POSTHOG_HOST,
      isAdmin: options.isAdmin || false,
      options: options.providerOptions,
    });
  }

  /**
   * Identify a user (call on login)
   */
  identify(userId, properties = {}) {
    if (this.debugMode) {
      console.log('[Analytics] identify:', userId, properties);
    }
    this.provider?.identify(userId, properties);
  }

  /**
   * Reset user identity (call on logout)
   */
  reset() {
    if (this.debugMode) {
      console.log('[Analytics] reset');
    }
    this.provider?.reset();
  }

  /**
   * Track an event
   */
  track(eventName, properties = {}) {
    if (this.debugMode) {
      console.log('[Analytics] track:', eventName, properties);
    }
    this.provider?.track(eventName, properties);
  }

  /**
   * Track a page view
   */
  page(pageName, properties = {}) {
    if (this.debugMode) {
      console.log('[Analytics] page:', pageName, properties);
    }
    this.provider?.page(pageName, properties);
  }

  /**
   * Set user properties
   */
  setUserProperties(properties) {
    if (this.debugMode) {
      console.log('[Analytics] setUserProperties:', properties);
    }
    this.provider?.setUserProperties(properties);
  }

  /**
   * Associate user with a group (e.g., household)
   * @param {string} groupType - Type of group (e.g., 'household')
   * @param {string} groupId - Unique identifier for the group
   * @param {Object} properties - Optional group properties
   */
  group(groupType, groupId, properties = {}) {
    if (this.debugMode) {
      console.log('[Analytics] group:', groupType, groupId, properties);
    }
    this.provider?.group(groupType, groupId, properties);
  }

  /**
   * Update session recording state based on user role
   * @param {boolean} isAdmin - Whether to disable session recording
   */
  updateSessionRecording(isAdmin) {
    if (this.debugMode) {
      console.log('[Analytics] updateSessionRecording:', { isAdmin });
    }
    this.provider?.updateSessionRecording?.(isAdmin);
  }

  /**
   * Check if analytics is enabled
   */
  isEnabled() {
    return this.provider?.isEnabled() ?? false;
  }
}

// Export singleton instance
const analytics = new AnalyticsService();
export default analytics;
